import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Link } from '@mui/material'
import { errorLog } from '../../../common/logger/Logger'
import { httpPost } from '../../../services/api/AmplifyApi'
import { viewId, titleName, restApiKey, masterClassification } from '../../../_constants/Code'
import { PathInfoList } from '../../../_constants/PathInfoList'
import { useModal } from '../../../_components/Modal'
import RelatedDashboard from '../parts/RelatedDashboard'
import { registerLocale } from 'react-datepicker'
import CheckboxRountedIcon from '@mui/icons-material/CheckBoxRounded'
import Checkbox from '@mui/material/Checkbox'
import ja from 'date-fns/locale/ja'
import { CopyButton } from '../../common/CustomButton'
import 'react-datepicker/dist/react-datepicker.css'
import { getDataCreateTask } from '../../../common/utils/DynamoDbUtils'
import { periodKind, taskStatus, dataFixStatus } from '../../../_constants/Code'
import FiscalDatePicker from './components/FiscalDatePicker'
import { getPeriodYm, formatDateToString, getFiscalPeriodRange } from './extra/dateUtils'
import Forbidden from '../../../_components/Forbidden'
import { ATTRIBUTE_MAPPING, convertToFileData } from './extra/DataUpload'
import { getMessage } from '../../../common/utils/MessageUtils'
import { parseStandardFormatSecondment } from './extra/dataParsers'
import { ChrofyCustomSelect } from '../../common/ChrofyCustomSelect'
import { fetchFiscalYearList, fetchJsonData, fetchMasterData } from './extra/services'
import { validateFiscalDateInput } from './extra/validation'

import { produce } from 'immer'
import { convertFormStateToPageState } from './extra/helpers'
// 日本語ロケールを登録
registerLocale('ja', ja)

/** AnnualIncomeContentコンポーネント */
const AnnualIncomeContent = (props) => {
  // 各種状態管理（ステート）
  const [isLoaded, setIsLoaded] = useState(false) // データの読み込み完了フラグ
  const [csvData, setCsvData] = useState([]) // CSVデータ
  const [master, setMaster] = useState([]) // マスター情報
  const [fiscalYearList, setFiscalYearList] = useState([]) // 年度リスト
  const [pageState, setPageState] = useState({})

  const [serverError, setServerError] = useState({}) // サーバーエラー情報
  const [inputError, setInputError] = useState({}) // 入力エラー情報
  const [isOpenFiscalYear, setIsOpenFiscalYear] = useState(false) // 年度ドロップダウン開閉状態
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('') // 選択中の年度
  const [isConfirmed, setIsConfirmed] = useState(true) // データ確定状態フラグ
  const [confirmErrorMessage, setConfirmErrorMessage] = useState('') // 確定時のエラーメッセージ
  const [dataCreateTask, setDataCreateTask] = useState([])
  const [conditions, setConditions] = useState({
    joinDate: false, // 入社年月日除外設定
    retireDate: false, // 退職年月日除外設定
    leaveCount: false, // 休職者判定の有無
    leaveCountType: '', // 休職判定タイプ
    leaveCountTimes: '' // 休職回数
  })
  const [joinDateValue, setJoinDateValue] = useState(null) // 入社年月日の日付値
  const [retireDateValue, setRetireDateValue] = useState(null) // 退職年月日の日付値
  const [aggregationTarget, setAggregationTarget] = useState('未選択') // 集計先設定
  const [fiscalPeriodRange, setFiscalPeriodRange] = useState({ minDate: null, maxDate: null }) // 決算期間の表示制限
  const [isOpenLeaveCountDropdown, setIsOpenLeaveCountDropdown] = useState(false)
  const [hasPreviousYearData, setHasPreviousYearData] = useState(false)
  const [standardFormatSecondmentList, setStandardFormatSecondmentList] = useState([])

  const isStdSecondmentExist = standardFormatSecondmentList.includes(selectedFiscalYear)

  const createPageInitialState = (csvData) => {
    const filteredDataMap = {}
    for (const data of csvData) {
      const joinDate = data['入社年月日'] ? new Date(data['入社年月日']) : null
      const isJoinDateEnabled = !!data['入社年月日']
      const retireDate = data['退職年月日'] ? new Date(data['退職年月日']) : null
      const isRetireDateEnabled = !!data['退職年月日']
      const leaveCount = !!data['休職者判定']
      const leaveCountTimes = data['休職回数'] || ''
      const leaveCountType = data['休職者判定'] === '1' ? 'times' : data['休職者判定'] === '2' ? 'endPoint' : ''
      const aggregationTarget = mapAggregationTargetReverse(data['集計先']) || ''

      const yearState = {
        joinDate,
        isJoinDateEnabled,
        retireDate,
        isRetireDateEnabled,
        leaveCount,
        leaveCountTimes,
        leaveCountType,
        aggregationTarget
      }

      filteredDataMap[data['年度']] = yearState
    }

    return filteredDataMap
  }

  // 選択年度またはCSVデータが変化したタイミングで処理を実行
  useEffect(() => {
    if (!selectedFiscalYear || fiscalYearList.length === 0) {
      setHasPreviousYearData(false)
      return
    }

    // fiscalYearList から年度文字列を取得し、整数に変換する
    const yearsIntList = fiscalYearList.map((yearStr) => parseInt(yearStr.replace('年度', ''), 10))
    const minYear = Math.min(...yearsIntList)
    // 選択中の年度を整数に変換
    const selectedYearInt = parseInt(selectedFiscalYear.replace('年度', ''), 10)
    // 選択した年度が最小年度の場合のみ、コピー用ボタンを無効化
    setHasPreviousYearData(selectedYearInt !== minYear)
    // 選択された年度の確定状況をチェック
    checkConfirmedStatus(selectedYearInt, props.accPeriod).then((confirmed) => {
      setIsConfirmed(confirmed)
      const message = confirmed ? '対象の年度は確定済みのため、編集できません。' : ''
      setConfirmErrorMessage(message) // 確定済みの場合、編集不可のメッセージを設定
    })

    // 選択された年度に基づいてデータを抽出
    // const filteredData = csvData.filter((item) => item['年度'] === selectedFiscalYear)

    const selectedYearData = pageState?.[selectedFiscalYear] || null

    if (selectedYearData !== null) {
      const {
        joinDate,
        retireDate,
        leaveCount,
        leaveCountType,
        leaveCountTimes,
        isJoinDateEnabled,
        isRetireDateEnabled,
        aggregationTarget
      } = selectedYearData

      setJoinDateValue(joinDate)
      setRetireDateValue(retireDate)
      setConditions({
        joinDate: isJoinDateEnabled,
        retireDate: isRetireDateEnabled,
        leaveCount,
        leaveCountType,
        leaveCountTimes
      })

      setAggregationTarget(aggregationTarget)
    } else {
      // データがない場合はリセット
      setJoinDateValue(null)
      setRetireDateValue(null)
      setConditions({
        joinDate: false,
        retireDate: false,
        leaveCount: false,
        leaveCountType: '',
        leaveCountTimes: ''
      })
      setAggregationTarget('')
    }
  }, [selectedFiscalYear, csvData, fiscalYearList, dataCreateTask])

  /**
   * データが確定しているかをチェックする非同期関数
   *
   * @param {number} year - 対象年度（西暦4桁）
   * @returns {Promise<boolean>} 確定していればtrue
   */
  const checkConfirmedStatus = async (year, accPeriod) => {
    const accPeriodMatch = accPeriod.match(/^(\d{1,2})月$/)
    if (!accPeriodMatch) throw new Error('決算月のフォーマット不正')
    const _accPeriod = Number(accPeriodMatch[1])

    try {
      // 各種期間種別で確定状態を判定
      const isPeriodConfirmed = (year) => {
        return dataCreateTask.some((task) => {
          const isPeriod = task.PeriodKind === periodKind.year.value
          const isStatusValid = [taskStatus.doing.value, taskStatus.done.value].includes(task.TaskStatus)
          const taskYearMatch = task.PeriodValue && task.PeriodValue.match(/^(\d{4})年/)
          const taskYear = Number(taskYearMatch ? taskYearMatch[1] : null)
          const _taskYear = _accPeriod === 12 ? taskYear : taskYear - 1
          const isYearMatch = _taskYear === year
          const isConfirmed = task.DataFixStatus === dataFixStatus.fix.value
          return isPeriod && isStatusValid && isYearMatch && isConfirmed
        })
      }

      // 年次が確定しているか判定
      const yearlyConfirmed = isPeriodConfirmed(year)
      return yearlyConfirmed
    } catch (error) {
      console.error('確認状態の取得に失敗しました。', error)
      return false
    }
  }

  // 集計先を選択
  const selectAggregationTarget = (value) => {
    setAggregationTarget(value)
  }
  // チェックボックス変更時のハンドラー
  const handleCheckboxChange = (key) => {
    setConditions((prev) => {
      const newValue = !prev[key]

      // チェックが外れた値値をクリア
      if (!newValue) {
        if (key === 'joinDate') setJoinDateValue(null)
        else if (key === 'retireDate') setRetireDateValue(null)
        else if (key === 'leaveCount') {
          // チェックを外したときに、ラジオボタンと休暇回数を初期状態にリセットする
          return {
            ...prev,
            leaveCount: newValue,
            leaveCountType: '', // radioボタンリセット
            leaveCountTimes: '' //  休職回数リセット
          }
        }
      }

      return { ...prev, [key]: newValue }
    })
  }

  // 入力変更時のハンドラー
  const handleInputChange = (key, value) => {
    setConditions((prev) => ({ ...prev, [key]: value }))
  }

  /**
   * 指定キーの入力エラーをクリアする（個別エラーが全てなくなったら総合エラーも削除）
   *
   * @param {string} key - エラーキー（joinDate / retireDate）
   */
  const clearInputError = (key) => {
    setInputError((prev) => {
      if (!(key in prev)) return prev
      const newErrors = { ...prev }
      delete newErrors[key]
      const keys = Object.keys(newErrors).filter((k) => k !== 'errorMessage')
      if (keys.length === 0) {
        delete newErrors.errorMessage
      }
      return newErrors
    })
  }

  /**
   * 日付入力欄の値変更ハンドラー。日付が確定したら該当の範囲外エラーをクリアする
   *
   * @param {string} key - 対象キー（joinDate / retireDate）
   * @param {Date | null} date - 変更後の日付
   */
  const handleDateChange = (key, date) => {
    if (key === 'joinDate') setJoinDateValue(date)
    else setRetireDateValue(date)
    if (date) clearInputError(key)
  }

  /**
   * 日付入力欄のフォーカスアウトハンドラー
   *
   * 手入力されたテキストが年度期間外またはパース不能な場合は入力を空にし、 「年度内の日付を登録してください。」エラーを表示する
   *
   * @param {string} key - 対象キー（joinDate / retireDate）
   * @param {string | null} rawText - 未確定の手入力テキスト（手入力がなければnull）
   */
  const handleDateFocusOut = (key, rawText) => {
    if (rawText === null) return
    const result = validateFiscalDateInput(rawText, fiscalPeriodRange.minDate, fiscalPeriodRange.maxDate)
    if (result.isEmpty) {
      clearInputError(key)
      return
    }
    if (result.isValid) {
      handleDateChange(key, result.date)
    } else {
      handleDateChange(key, null)
      setInputError((prev) => ({ ...prev, [key]: getMessage('E011024') }))
    }
  }
  // 休職回数選択以外を選択した場合は休職回数をリセット
  useEffect(() => {
    if (conditions.leaveCountType !== 'times' && conditions.leaveCountTimes !== '') {
      setConditions((prev) => ({ ...prev, leaveCountTimes: '' }))
    }
  }, [conditions.leaveCountType])

  /** 集計先の画面表示用と内部値の相互変換関数 */
  const mapAggregationTarget = (value) => {
    switch (value) {
      case '出向元企業の集計に含める':
        return '出向元'
      case '出向先企業の集計に含める':
        return '出向先'
      case '出向元企業と出向先企業の両方に含めない':
        return '含めない'
      case '未選択': // 追加: デフォルト値
        return null
      default:
        return ''
    }
  }
  // 集計先の値を内部表現から画面表示用に変換する関数（逆変換）
  const mapAggregationTargetReverse = (value) => {
    switch (value) {
      case '出向元':
        return '出向元企業の集計に含める'
      case '出向先':
        return '出向先企業の集計に含める'
      case '含めない':
        return '出向元企業と出向先企業の両方に含めない'
      case null: // 追加: デフォルト値に対応
        return '未選択'
      default:
        return ''
    }
  }

  // 初期処理
  useEffect(() => {
    document.title = props.pageTitle
    window.scrollTo(0, 0)
    init()
  }, [])

  // 初期処理完了後にスクロールバー設定
  useEffect(() => {
    if (isLoaded) setScrollBar()
  }, [isLoaded])

  // 入社年月日のチェックボックス解除時に該当エラーをクリア
  useEffect(() => {
    if (!conditions.joinDate && inputError.joinDate) {
      setInputError((prev) => {
        const newErrors = { ...prev }
        delete newErrors.joinDate
        const keys = Object.keys(newErrors).filter((key) => key !== 'errorMessage')
        if (keys.length === 0) {
          delete newErrors.errorMessage
        }

        return newErrors
      })
    }
  }, [conditions.joinDate, inputError.joinDate])

  // 退職年月日のチェックボックス解除時に該当エラーをクリア
  useEffect(() => {
    if (!conditions.retireDate && inputError.retireDate) {
      setInputError((prev) => {
        const newErrors = { ...prev }
        delete newErrors.retireDate

        const keys = Object.keys(newErrors).filter((key) => key !== 'errorMessage')
        if (keys.length === 0) {
          delete newErrors.errorMessage
        }

        return newErrors
      })
    }
  }, [conditions.retireDate, inputError.retireDate])

  // 休職チェックボックス解除時に該当エラーをクリア
  useEffect(() => {
    if (!conditions.leaveCount && inputError.leaveCountType) {
      setInputError((prev) => {
        const newErrors = { ...prev }
        delete newErrors.leaveCountType

        // 個別エラーが全てなくなったら総合エラーも削除
        const keys = Object.keys(newErrors).filter((key) => key !== 'errorMessage')
        if (keys.length === 0) {
          delete newErrors.errorMessage
        }

        return newErrors
      })
    }
  }, [conditions.leaveCount, inputError.leaveCountType])

  // 選択年度に応じてDatePickerの表示期間を設定
  useEffect(() => {
    if (selectedFiscalYear && props.accPeriod) {
      const periodLabel = getPeriodYm(props.accPeriod, selectedFiscalYear)
      const { minDate, maxDate } = getFiscalPeriodRange(periodLabel)
      setFiscalPeriodRange({ minDate, maxDate })
    }
  }, [selectedFiscalYear, props.accPeriod])

  const calculateCurrentYear = (props) => {
    // 現在の年度を判定し年度リストを作成
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    return `${props.accPeriod !== '12月' && month <= parseInt(props.accPeriod.slice(0, -1), 10) ? year - 1 : year}年度`
  }

  // 初期処理でcsvDataをセット
  const init = async () => {
    // マスター情報取得
    const currentYear = calculateCurrentYear(props)
    const _masterData = fetchMasterData(props)
    const _dataCreateTask = getDataCreateTask(props.corporationNumber, props.corporationNumber)
    const _parsedSecondmentList = parseStandardFormatSecondment(props.corporationNumber)
    const _jsonData = fetchJsonData(props)

    const [masterData, data, parsedSecondmentList, jsonData] = await Promise.all([
      _masterData,
      _dataCreateTask,
      _parsedSecondmentList,
      _jsonData
    ])

    const yearList = await fetchFiscalYearList(currentYear, props)

    setMaster(masterData)
    setCsvData(jsonData)
    setDataCreateTask(data)
    setStandardFormatSecondmentList(parsedSecondmentList)
    setFiscalYearList(yearList)

    // 年収計算情報設定
    if (yearList && yearList.length > 0) {
      setFiscalYearList(yearList)
      setSelectedFiscalYear(currentYear)
      setPageState(createPageInitialState(jsonData))
    }

    setIsLoaded(true)
  }

  /** 選択年度更新処理 */
  const updateSelectedFiscalYear = (value) => {
    setInputError({})
    setSelectedFiscalYear(value)
    toggleFiscalYearDropdown(false)
  }
  /** 画面に登録データを反映するためのデータリスト作成 */
  const getDataList = () => {
    const leaveJudgement = conditions.leaveCount
      ? conditions.leaveCountType === 'times'
        ? '1'
        : conditions.leaveCountType === 'endPoint'
          ? '2'
          : ''
      : ''
    const leaveTimes =
      conditions.leaveCount && conditions.leaveCountType === 'times' ? String(conditions.leaveCountTimes) : ''
    // 決算期の文字列（"YYYY年M月期" 形式、例: "2027年12月期"）を作成する
    const periodLabel = getPeriodYm(props.accPeriod, selectedFiscalYear)
    // 決算期の月末日を取得する
    const { maxDate } = getFiscalPeriodRange(periodLabel)
    // 月末日を "YYYY-MM-DD" の文字列に変換する
    const fiscalYearMonthStr = formatDateToString(maxDate)

    return [
      {
        法人番号: props.corporationNumber,
        企業名: props.corporationName,
        年度: selectedFiscalYear,
        決算年月: fiscalYearMonthStr,
        入社年月日: formatDateToString(joinDateValue),
        退職年月日: formatDateToString(retireDateValue),
        休職者判定: leaveJudgement,
        休職回数: leaveTimes,
        集計先: mapAggregationTarget(aggregationTarget)
      }
    ]
  }

  /**
   * 年度ドロップダウンの開閉を切り替え
   *
   * @param {boolean} isOpen - ドロップダウン開閉判定
   */
  const toggleFiscalYearDropdown = (isOpen) => {
    setIsOpenFiscalYear(isOpen)
  }

  /**
   * 年収計算設定テーブルを左にスクロール
   *
   * @param {Object} e - イベントオブジェクト
   */
  const scrollLeft = (e) => {
    e.target
      .closest('.setting-table')
      .querySelector('.setting-table__scroll')
      .scrollBy({ left: -130, behavior: 'smooth' })
  }

  /** @param {Object} e - イベントオブジェクト */
  const scrollRight = (e) => {
    e.target
      .closest('.setting-table')
      .querySelector('.setting-table__scroll')
      .scrollBy({ left: 130, behavior: 'smooth' })
  }

  /** スクロールバー設定 */
  const setScrollBar = () => {
    // ＜ボタンにdisabledをつける
    document.querySelectorAll('.setting-table__btn-scroll-left').forEach((v) => v.setAttribute('disabled', ''))
    // スクロールイベント登録
    document.querySelectorAll('.setting-table__scroll').forEach((element) => {
      if (element.classList.contains('is-scroll')) return
      element.addEventListener('scroll', (event) => {
        // 要素の取得
        const $parent = event.target.closest('.setting-table')
        const $tableWrapper = $parent.querySelector('.setting-table__scroll')
        const $btnScrollLeft = $parent.querySelector('.setting-table__btn-scroll-left')
        const $btnScrollRight = $parent.querySelector('.setting-table__btn-scroll-right')
        const isScrollEnd = $tableWrapper.scrollWidth - ($tableWrapper.scrollLeft + $tableWrapper.clientWidth) < 1

        $btnScrollLeft.toggleAttribute('disabled', $tableWrapper.scrollLeft === 0)
        $btnScrollRight.toggleAttribute('disabled', isScrollEnd)
      })
      element.classList.add('is-scroll')
    })
  }

  useEffect(() => {
    if (fiscalYearList.length === 0) return

    const _pageState = convertFormStateToPageState({
      joinDate: joinDateValue,
      retireDate: retireDateValue,
      aggregationTarget,
      conditions
    })

    setPageState(
      produce((draft) => {
        const selectedYear = draft[selectedFiscalYear]
        if (!selectedYear) {
          draft[selectedFiscalYear] = _pageState
        } else {
          Object.assign(selectedYear, _pageState)
        }
      })
    )
  }, [joinDateValue, retireDateValue, conditions, selectedFiscalYear, aggregationTarget, fiscalYearList])

  const resetForm = () => {
    setJoinDateValue(null)
    setRetireDateValue(null)
    setAggregationTarget('')
    setConditions({
      joinDate: false,
      retireDate: false,
      leaveCount: '',
      leaveCountType: '',
      leaveCountTimes: ''
    })
  }

  const addYear = (dateStr) => {
    if (!dateStr) return null
    try {
      const d = new Date(dateStr)
      const month = d.getMonth() // 0-based: 1 = February
      const date = d.getDate()
      const year = d.getFullYear()
      const newYear = year + 1

      let newDate = new Date(d)
      newDate.setFullYear(newYear)

      // 元の日付が2月29日かをチェック
      if (month === 1 && date === 29) {
        const lastDayOfFeb = new Date(newYear, 2, 0).getDate() // 28 また29
        newDate = new Date(newYear, 1, lastDayOfFeb) // yyyy-02-28 また yyyy-02-29
      }

      return newDate
    } catch {
      return null
    }
  }

  /**
   * 前期設定コピー処理
   *
   * 選択されている年度の前年データをAPIから取得し、 取得したデータを画面の各入力項目に反映する
   */
  const handleCopyPreviousSettings = async () => {
    if (!selectedFiscalYear) return

    const prevYearNum = parseInt(selectedFiscalYear.replace('年度', ''), 10) - 1
    const prevYear = `${prevYearNum}年度`

    const prevData = pageState[prevYear]
    if (!prevData) {
      resetForm()
      return Promise.resolve()
    }
    setJoinDateValue(addYear(prevData.joinDate))
    setRetireDateValue(addYear(prevData.retireDate))

    let leaveChecked = prevData.leaveCount
    let leaveType = prevData.leaveCountType
    let leaveTimes = prevData.leaveCountTimes

    setConditions((prev) => ({
      ...prev,
      joinDate: prevData.isJoinDateEnabled,
      retireDate: prevData.isRetireDateEnabled,
      leaveCount: leaveChecked,
      leaveCountType: leaveType,
      leaveCountTimes: leaveTimes
    }))

    setAggregationTarget(prevData.aggregationTarget || '')
    return Promise.resolve()
  }

  /**
   * 入力チェック
   *
   * @returns {boolean} エラー判定
   */
  const _checkInput = () => {
    const errors = {}

    // 入社年月日
    if (conditions.joinDate && (!joinDateValue || formatDateToString(joinDateValue) === '')) {
      errors.joinDate = getMessage('E010001', '除外設定（入社年月日）')
    }

    // 退職年月日
    if (conditions.retireDate && (!retireDateValue || formatDateToString(retireDateValue) === '')) {
      errors.retireDate = getMessage('E010001', '除外設定（退職年月日）')
    }

    // 休職の有無・回数で指定
    if (conditions.leaveCount && !conditions.leaveCountType) {
      errors.leaveCountType = getMessage('E010017', '除外設定')
    } else if (conditions.leaveCountType === 'times' && !conditions.leaveCountTimes) {
      errors.leaveCountType = getMessage('E010001', '休職回数')
    }

    if (aggregationTarget === '') {
      errors.aggregationTarget = getMessage('E010017', '集計先')
    }

    if (Object.keys(errors).length > 0) {
      setInputError({ errorMessage: '入力内容に誤りがあります。', ...errors })
      return true
    } else {
      setInputError({})
      return false
    }
  }
  const isLeaveCountTimesError = inputError.leaveCountType === getMessage('E010001', '休職回数')

  /** 年収計算設定の更新処理 入力チェック後、APIにデータを送信する */
  const handleUpdateAnnualIncome = async () => {
    if (_checkInput()) {
      window.scrollTo(0, 0)
      return
    }

    try {
      const title = 'マスター設定値登録の確認'
      const description = (
        <>
          <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
          {!isStdSecondmentExist && (
            <div className="pt-5 text-red-600 text-center">
              ※対象決算期の出向情報がアップロードされていません。
              <br />
              兼務出向の判定が行えないため、年収が正しく集計されない可能性があります。
            </div>
          )}
          <div className="pt-5 text-center">
            ※ダッシュボードへの反映には5分～10分程度かかります
            <br />
            マスター管理画面のステータスから反映状況を確認してください
          </div>
        </>
      )

      const buttons = [
        {
          name: '実行',
          className: 'footer-btn button_base',
          click: async () => {
            try {
              setServerError({})
              const dataList = getDataList()

              const convertDataList = convertToFileData(dataList, ATTRIBUTE_MAPPING, conditions)
              const init = {
                body: {
                  request: {
                    corporation_number: props.corporationNumber,
                    corporation_name: props.corporationName,
                    user_name: props.userName,
                    fiscal_month: props.accPeriod,
                    file_data: convertDataList,
                    user_sub: props.userId,
                    year: String(selectedFiscalYear).replaceAll(/\D/g, '')
                  }
                },
                headers: {}
              }
              await httpPost(
                restApiKey.CHROFY_REST_API,
                PathInfoList.Resource.updateAnnualIncomeCalculation(props.corporationNumber),
                init
              ).then((res) => {
                if (res.message.indexOf('Success') !== -1) {
                  props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
                } else {
                  setServerError({ errorMessage: '更新処理でエラーが発生しました。' })
                  window.scrollTo(0, 0)
                }
              })
            } catch (error) {
              errorLog(
                'E000001',
                props.userId,
                viewId.annualIncomeCalculationInfo,
                'updateAnnualIncomeCalculation',
                error
              )
            }
          }
        }
      ]
      props.showModal({ title, description, button: buttons, isWide: true })
    } catch (error) {
      errorLog('E000001', props.userId, viewId.annualIncomeCalculationInfo, 'updateAnnualIncomeCalculation', error)
    }
  }

  const secondmentOptions = [
    '出向元企業の集計に含める',
    '出向先企業の集計に含める',
    '出向元企業と出向先企業の両方に含めない'
  ].map((e) => ({ label: e, value: e }))

  /** 描画処理 */
  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>

            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification?.[master.Classification]?.name || ''}
              </h1>
            </header>

            {/*  コンテンツボディ */}
            <div className="page-body">
              <div>
                <span className="text-red-600">{inputError.errorMessage}</span>
                <span className="text-red-600">{serverError.errorMessage}</span>
              </div>

              {/*  ===== 平均年収の設定 ===== */}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  年収計算設定
                </h2>

                <p className="mt-[20px]">
                  年収計算を行うための条件を設定します。設定内容は有価証券報告書レポートおよび年収集計ダッシュボード（3.2
                  賃金制度の運用状況）に反映されます。
                </p>

                <div
                  className="box-graph__target-fiscal-year flex items-center"
                  style={{ marginTop: '20px', justifyContent: 'space-between' }}
                >
                  {/* 左側：「対象」とドロップダウン */}
                  <div className="flex items-center">
                    <div className="text-h5">対象：</div>
                    <div className="action-area" style={{ position: 'relative' }}>
                      <div className="year-container" tabIndex="0" onBlur={() => toggleFiscalYearDropdown(false)}>
                        <div
                          id="fiscalYearButton"
                          className={`yearButton flex items-center justify-between rounded-xl pl-4 pr-2 ${!isLoaded ? ' is-disabled' : ''}`}
                          style={{ paddingTop: '4px', paddingBottom: '4px', minWidth: '140px' }}
                          onClick={() => toggleFiscalYearDropdown(!isOpenFiscalYear)}
                        >
                          <div className="yearButton__text text-h7 mr-2">
                            {getPeriodYm(props.accPeriod, selectedFiscalYear)}
                          </div>
                          <div className="yearButton__icon flex items-center justify-center w-8 h-8">
                            <span
                              className={`icon-arrow transition-transform duration-200 ${isOpenFiscalYear ? '-rotate-180' : 'rotate-0'}`}
                            ></span>
                          </div>
                        </div>
                        <ul className="year rounded-xl" hidden={!isOpenFiscalYear || !isLoaded} style={{ zIndex: 30 }}>
                          {fiscalYearList.map((value, index) => (
                            <li key={index} className="item text-h7" onClick={() => updateSelectedFiscalYear(value)}>
                              {getPeriodYm(props.accPeriod, value)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* 右側：「前期設定コピー」ボタン */}
                  <div className="btn-group pl-10 border-l border-blue2-lighten-20">
                    <CopyButton onClick={handleCopyPreviousSettings} disabled={!hasPreviousYearData} />
                  </div>
                </div>
                <div
                  role="alert"
                  aria-live="assertive"
                  className={`text-red-600 min-h-[30px] ${isConfirmed ? 'visible my-2' : 'invisible'}`}
                  style={{ lineHeight: 1.2 }}
                >
                  {confirmErrorMessage}
                </div>

                <div class="border-t border-blue1 mb-4 -mx-16"></div>

                {/* ■年収計算の除外設定タイトル */}
                <div className="text-body-2-bold grow ">■年収計算の除外設定</div>
                <div className="flex items-center justify-between mb-6">
                  年収の集計対象から除外する条件を設定してください。
                </div>

                {/* 除外条件設定用テーブル */}
                <div className="setting-table">
                  <table
                    className="setting-table__table w-full max-w-[65dvw] form-table mt-4"
                    style={{ borderCollapse: 'collapse' }}
                  >
                    <colgroup>
                      <col className="w-[20%] min-w-[300px]" />
                      <col className="w-[80%] max-w-[700px]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="w-[100px] !p-[12px]  border-b border-[#ddd] !text-left">条件</th>
                        <th className="setting-table__col-master-Outline" style={{ textAlign: 'left' }}></th>
                      </tr>
                    </thead>

                    <tbody className="text-base">
                      {/* 入社年月日で指定 */}
                      <tr style={{ borderBottom: '1px solid #ddd' }}>
                        <th className="!p-0 h-px min-h-[20px] setting-table__col-name  ">
                          <div className="h-full flex flex-col justify-center ">
                            <label className="px-3 py-3 text-left w-[200px] inline-flex flex-nowrap items-center gap-2 text-base font-bold">
                              <Checkbox
                                size="small"
                                checkedIcon={<CheckboxRountedIcon />}
                                checked={conditions.joinDate}
                                onChange={() => handleCheckboxChange('joinDate')}
                                disabled={false}
                                inputProps={{ 'aria-label': '入社年月日で指定' }}
                              />
                              入社年月日で指定
                              {conditions.joinDate && <span className="red--text">*</span>}
                            </label>
                          </div>
                        </th>
                        <td className="!p-[12px] text-left align-middle">
                          {/* 入社年月日指定の詳細設定 */}
                          <div className="flex flex-col items-start space-y-1 ml-3">
                            <div className="flex items-center flex-wrap">
                              <span className="mr-2">入社年月日が</span>
                              <FiscalDatePicker
                                selected={joinDateValue}
                                onChange={(date) => handleDateChange('joinDate', date)}
                                onFocusOut={(rawText) => handleDateFocusOut('joinDate', rawText)}
                                disabled={!conditions.joinDate}
                                minDate={fiscalPeriodRange.minDate}
                                maxDate={fiscalPeriodRange.maxDate}
                                openToDate={fiscalPeriodRange.minDate}
                                className={
                                  `rounded-lg text-center h-[40px] pr-[20px] w-[150px] mx-2 ` +
                                  ` text-[14px] font-normal ${inputError.joinDate ? 'is-error' : ''}`
                                }
                                placeholderText="YYYY/MM/DD"
                              />
                              <span className="ml-2">以降の入職者を集計から除外する</span>
                            </div>
                            {inputError.joinDate && <div className="text-red-600 mt-1">{inputError.joinDate}</div>}
                          </div>
                        </td>
                      </tr>

                      {/* 退職年月日で指定 */}
                      <tr style={{ borderBottom: '1px solid #ddd' }}>
                        <th className="!p-0 h-px min-h-[20px] ">
                          <div className="h-full flex flex-col justify-center ">
                            <label className="px-3 py-3 text-left w-[200px] inline-flex flex-nowrap items-center gap-2 text-base font-bold">
                              <Checkbox
                                size="small"
                                checkedIcon={<CheckboxRountedIcon />}
                                checked={conditions.retireDate}
                                onChange={() => handleCheckboxChange('retireDate')}
                                disabled={false}
                                inputProps={{ 'aria-label': '退職年月日で指定' }}
                              />
                              退職年月日で指定
                              {conditions.retireDate && <span className="red--text">*</span>}
                            </label>
                          </div>
                        </th>
                        <td className="!p-[12px] text-left align-middle">
                          {/* 退職年月日指定の詳細設定 */}
                          <div className="flex flex-col items-start ml-3">
                            <div className="flex items-center flex-wrap">
                              <span style={{ marginRight: '8px' }}>退職年月日が</span>
                              <FiscalDatePicker
                                selected={retireDateValue}
                                onChange={(date) => handleDateChange('retireDate', date)}
                                onFocusOut={(rawText) => handleDateFocusOut('retireDate', rawText)}
                                disabled={!conditions.retireDate}
                                minDate={fiscalPeriodRange.minDate}
                                maxDate={fiscalPeriodRange.maxDate}
                                openToDate={fiscalPeriodRange.minDate}
                                className={
                                  `rounded-lg text-center h-[40px] pr-[20px] w-[150px] mx-2 ` +
                                  ` text-[14px] font-normal ${inputError.retireDate ? 'is-error' : ''}`
                                }
                                placeholderText="YYYY/MM/DD"
                              />
                              <span style={{ marginLeft: '8px' }}>以前の退職者を集計から除外する</span>
                            </div>
                            {inputError.retireDate && <div className="text-red-600 mt-1">{inputError.retireDate}</div>}
                          </div>
                        </td>
                      </tr>

                      {/* 休職従業員除外 */}
                      <tr className="border-b border-gray-300">
                        <th className="!p-0 h-px min-h-[20px] ">
                          <div className="h-full flex flex-col justify-center ">
                            <label className="px-3 py-3 text-left w-[200px] inline-flex flex-nowrap items-center gap-2 text-base font-bold">
                              <Checkbox
                                size="small"
                                checkedIcon={<CheckboxRountedIcon />}
                                checked={conditions.leaveCount}
                                onChange={() => handleCheckboxChange('leaveCount')}
                                disabled={false}
                                inputProps={{ 'aria-label': '休職の有無・回数で指定 ※' }}
                              />
                              休職の有無・回数で指定 ※{conditions.leaveCount && <span className="red--text">*</span>}
                            </label>
                          </div>
                        </th>
                        <td className="!p-[12px] text-left align-middle">
                          <div className="flex flex-col space-y-4 ml-3">
                            <div className="flex items-center">
                              <label className="ui-radio flex items-center">
                                <input
                                  type="radio"
                                  name="leaveCountType"
                                  value="times"
                                  checked={conditions.leaveCountType === 'times'}
                                  onChange={(e) => handleInputChange('leaveCountType', e.target.value)}
                                  className="ui-radio__input"
                                  disabled={!conditions.leaveCount}
                                />
                                <span className="ui-radio__icon"></span>
                                <span className="ui-radio__ripple"></span>
                                <span className="ui-radio__label text-h7 font-Regular flex items-center"></span>
                                <span>休職区分が設定された月の数が年間</span>
                                <div className="relative inline-block ml-2">
                                  <select
                                    value={conditions.leaveCountTimes}
                                    onChange={(e) => handleInputChange('leaveCountTimes', e.target.value)}
                                    className={`appearance-none ml-2 w-16 h-8 text-center border rounded-lg cursor-pointer text-[14px] font-normal pr-6 disabled:bg-gray-300 ${
                                      isLeaveCountTimesError ? 'is-error' : ''
                                    }`}
                                    disabled={!conditions.leaveCount || conditions.leaveCountType !== 'times'}
                                    aria-label="休職回数を選択"
                                    onClick={() => setIsOpenLeaveCountDropdown(!isOpenLeaveCountDropdown)}
                                    onBlur={() => setIsOpenLeaveCountDropdown(false)}
                                  >
                                    <option value=""></option>
                                    {[...Array(12)].map((_, i) => (
                                      <option key={i + 1} value={i + 1}>
                                        {i + 1}
                                      </option>
                                    ))}
                                  </select>{' '}
                                  <span
                                    className={`icon-arrow absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${isOpenLeaveCountDropdown ? '-rotate-90' : 'rotate-90'}`}
                                  ></span>
                                </div>
                                <span className="ml-1"> 回以上の従業員を除外する</span>
                              </label>
                            </div>
                            <div className="flex items-center">
                              <label className="ui-radio flex items-center ">
                                <input
                                  type="radio"
                                  name="leaveCountType"
                                  value="endPoint"
                                  checked={conditions.leaveCountType === 'endPoint'}
                                  onChange={(e) => handleInputChange('leaveCountType', e.target.value)}
                                  className="ui-radio__input"
                                  disabled={!conditions.leaveCount}
                                />
                                <span className="ui-radio__icon"></span>
                                <span className="ui-radio__ripple"></span>
                                <span className="ui-radio__label text-h7 font-Regular"></span>

                                <span> 期末時点休職者のみ除外する</span>
                              </label>
                            </div>
                            {inputError.leaveCountType && (
                              <div className="text-red-600 mt-1 text-left">{inputError.leaveCountType}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* 休職従業員除外の補足説明 */}
                <p className="text-caption-2 mt-2" style={{ fontSize: '15px', color: '#555' }}>
                  ※「休職区分」とは、対象者がその月のうち1日でも休職していた場合に付与されるフラグです。例えば、月の途中からの休職や一時的な休職であっても、その月全体に対して休職区分が設定されます。
                </p>

                {/* ■出向者の年収計算への集計設定 */}
                <div className="mt-8">
                  <div className="text-body-2-bold grow mt-6">■出向者の年収計算への集計設定</div>
                  <div className="flex items-center justify-between mb-6">
                    出向中の従業員の年収計算方法について、集計先を選択してください。
                  </div>
                  <div className="flex gap-x-1 ">
                    <ChrofyCustomSelect
                      lightNoSelect
                      options={secondmentOptions}
                      className="w-[350px]"
                      value={aggregationTarget === '未選択' ? '' : aggregationTarget}
                      onChange={(e) => {
                        const value = e.target.value || '未選択'
                        selectAggregationTarget(value)
                      }}
                    />
                    <span className="red--text">*</span>
                  </div>

                  {inputError.aggregationTarget && (
                    <div className="text-red-600 mt-1 text-left">{inputError.aggregationTarget}</div>
                  )}
                </div>

                {/* エラーメッセージ表示 */}
                <div className="content-footer mt-16 mb-8">
                  <div className="border-t border-blue1" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    <button
                      className="btn footer-btn button_base"
                      onClick={handleUpdateAnnualIncome}
                      disabled={isConfirmed}
                      aria-disabled={isConfirmed}
                    >
                      <div>登録</div>
                    </button>
                  </div>
                </div>
              </div>
              {/*  ===== 関連ダッシュボード ===== */}
              <RelatedDashboard dashboards={master.RelatedDashboard} authority={props.authority} mode={props.mode} />
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

/** AnnualIncomeCalculationInfoコンポーネント ユーザーの権限に基づいて表示制御を行う */
const AnnualIncomeCalculationInfo = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()

  // 権限がない場合はForbiddenコンポーネントを表示
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Dashboard.AnnualIncomeCalculation_Set
  ) {
    return <Forbidden />
  }

  return (
    <AnnualIncomeContent
      pageTitle={titleName.AnnualIncomeCalculationInfo + titleName.common}
      corporationNumber={state.company.corporationNumber}
      corporationName={state.company.name}
      bucketName={'chrofy-' + process.env.REACT_APP_ENV + '-' + state.company.corporationNumber}
      accPeriod={state.company.accPeriod}
      userId={state.user.sub}
      userName={state.user.userName}
      authority={state.authority}
      mode={state.analysis.mode}
      navigate={navigate}
      showModal={showModal}
    />
  )
}

export default AnnualIncomeCalculationInfo
